// CPK-3620 — postpone helper (frontend mirror of the backend kycPostpone).
//
// A FAILED KYC check whose notes carry a "Check later" entry
// (data.postpone_reason.canProceed === true) is allowed to proceed: the client
// continues the sign-up even though the check is FAIL. Keep this in sync with
// lambdas/src/endpoint/core/kyc-check/kycPostpone.ts (notesAllowProceed).

import { MessageDocument } from '../models/message-blocks';

export interface PostponeReason {
  errors: string[];
  postpone_list: string[];
  canProceed: boolean;
  messages: { PT: string; EN: string };
  /**
   * Optional block-based message (CPK-3802). When present, the success screen
   * renders this via <app-message-renderer> instead of the plain `messages`
   * text. Carried from cp_actions.config.postpone.blocks by the backend.
   */
  blocks?: MessageDocument;
}

/** True if any note in a cp_kyc_check.notes array marks the check as proceed-allowed. */
export function notesAllowProceed(notes: unknown): boolean {
  if (!Array.isArray(notes)) return false;
  return notes.some((n: any) => n?.data?.postpone_reason?.canProceed === true);
}

/** Returns the postpone_reason from the first "Check later" note, or null. */
export function getPostponeReason(notes: unknown): PostponeReason | null {
  if (!Array.isArray(notes)) return null;
  const note = notes.find((n: any) => n?.data?.postpone_reason?.canProceed === true);
  return note ? ((note as any).data.postpone_reason as PostponeReason) : null;
}

/** Localized postpone message ('PT' | 'EN'), or '' when there is none. */
export function getPostponeMessage(notes: unknown, lang: "PT" | "EN" = "EN"): string {
  const reason = getPostponeReason(notes);
  return reason?.messages?.[lang] ?? "";
}

/** Block-based postpone message, or null when the action only configured plain text. */
export function getPostponeBlocks(notes: unknown): MessageDocument | null {
  const reason = getPostponeReason(notes);
  const doc = reason?.blocks;
  return doc && Array.isArray(doc.blocks) ? doc : null;
}

// ==================== TC6 — resubmit-vs-manual-review choice ====================

/**
 * Error tokens meaning the document could not be READ (as opposed to a
 * compliance postpone like PEP/suspicious). Only these warrant the TC6
 * "resubmit a clearer document vs refer to manual review" informed-consent
 * choice — resubmitting can genuinely fix them.
 */
export const READABILITY_POSTPONE_TOKENS = [
  'resubmission',
  'document_expired',
  'could_not_get_cpf',
];

/** True when a postpone note's error tokens include a readability token. */
export function postponeErrorsIncludeReadability(notes: unknown): boolean {
  if (!Array.isArray(notes)) return false;
  return notes.some((n: any) => {
    const errors: string[] = n?.data?.postpone_reason?.errors ?? [];
    return Array.isArray(errors) && errors.some((e) => READABILITY_POSTPONE_TOKENS.includes(e));
  });
}

/**
 * CPK-4325 — true when the postpone was FORCED by a failed SERPRO/CPF registry
 * under `registryStatusPolicy`/`registryFailurePolicy` = "postpone" (the backend
 * stamps `provider_return.registry_failure`). It reuses the could_not_get_cpf
 * token, but resubmitting can never change the outcome — the check finalizes
 * FAIL+canProceed no matter the document.
 */
export function isRegistryForcedPostpone(providerReturn: any): boolean {
  return providerReturn?.registry_failure?.policy === 'postpone';
}

/**
 * TC6 gate: offer the resubmit-vs-manual-review choice only for readability
 * postpones that were NOT forced by the SERPRO registry — a registry-forced
 * postpone renders the plain postpone screen (message + Proceed), exactly like
 * any other postpone.
 */
export function postponeOffersRetryChoice(
  isPostpone: boolean,
  notes: unknown,
  providerReturn: any
): boolean {
  return isPostpone && !isRegistryForcedPostpone(providerReturn) && postponeErrorsIncludeReadability(notes);
}

// ==================== CPK-4807 — retry from the outstanding action ====================
//
// CPK-4442 made a postponed KYC clickable again in the outstanding-actions card, but
// the reopened modal landed on the postpone screen whose only button ("Prosseguir")
// just closes the dialog — the client could never retry. When the modal is opened
// from the outstanding action, the postpone screen offers a resubmit while the client
// still has retries, and a plain "Fechar" once they are spent. The guide wizard and a
// fresh submission (no flag) keep the plain "Prosseguir" exactly as before.

export type OaPostponeFooter = 'proceed' | 'retry' | 'close';

export interface OaPostponeFooterInput {
  reopenedFromOutstandingAction?: boolean;
  isPostpone: boolean;
  /** TC6 choice already renders its own resubmit / manual-review buttons. */
  offersRetryChoice: boolean;
  retriesLoading: boolean;
  currentRetries: number;
  maxRetries: number;
}

/** Which footer the postpone (SUCCESS) screen renders. */
export function oaPostponeFooter(input: OaPostponeFooterInput): OaPostponeFooter {
  if (!input.reopenedFromOutstandingAction || !input.isPostpone || input.offersRetryChoice) {
    return 'proceed';
  }
  // Retry count still loading: keep the neutral footer until we know which one applies.
  if (input.retriesLoading) return 'proceed';
  const hasRetriesLeft = input.maxRetries === Infinity || input.currentRetries < input.maxRetries;
  return hasRetriesLeft ? 'retry' : 'close';
}

/**
 * When the retry limit is reached on a postpone reopened from the outstanding action,
 * the CLIENT stays on the postpone screen (with "Fechar") instead of being switched to
 * RETRY_LIMIT_EXCEEDED. An impersonating agent still goes to RETRY_LIMIT_EXCEEDED, which
 * carries the skip / increase-attempts card.
 */
export function holdPostponeOnRetryLimit(
  reopenedFromOutstandingAction: boolean | undefined,
  isPostpone: boolean,
  isAgentSession: boolean
): boolean {
  return !!reopenedFromOutstandingAction && isPostpone && !isAgentSession;
}

// ==================== REVIEW (terminal, no proceed) ====================
//
// A FAILED KYC check that is NOT postponable (no canProceed) but is a
// compliance/watchlist block routes to the REVIEW terminal ("we'll review your
// case and get back to you"). The backend copies cp_actions.config.review.
// {messages,blocks} onto the terminal note as `data.review_reason`, mirroring how
// config.postpone.{messages,blocks} become `data.postpone_reason`. Keep in sync
// with the backend note emission.

export interface ReviewReason {
  /** Localized manual-review copy from cp_actions.config.review.messages. */
  messages?: { PT: string; EN: string };
  /** Optional block-based message from cp_actions.config.review.blocks. */
  blocks?: MessageDocument;
}

/** Returns the review_reason from the first note that carries one, or null. */
export function getReviewReason(notes: unknown): ReviewReason | null {
  if (!Array.isArray(notes)) return null;
  const note = notes.find((n: any) => n?.data?.review_reason != null);
  return note ? ((note as any).data.review_reason as ReviewReason) : null;
}

/**
 * True when any note carries a `review_reason` — i.e. the backend flagged this
 * terminal as a manual-review block. Used (alongside the watchlist detector) to
 * route the genuine blocked case to the REVIEW terminal instead of the generic
 * inline error card.
 */
export function notesRequireReview(notes: unknown): boolean {
  return getReviewReason(notes) != null;
}

/** Localized review message ('PT' | 'EN'), or '' when there is none. */
export function getReviewMessage(notes: unknown, lang: "PT" | "EN" = "EN"): string {
  const reason = getReviewReason(notes);
  return reason?.messages?.[lang] ?? "";
}

/** Block-based review message, or null when the action only configured plain text. */
export function getReviewBlocks(notes: unknown): MessageDocument | null {
  const reason = getReviewReason(notes);
  const doc = reason?.blocks;
  return doc && Array.isArray(doc.blocks) ? doc : null;
}

// ==================== AGENT SKIP (CPK-4051) ====================
//
// An agent-driven skip writes a cp_kyc_check with stage=SKIPPED_BY_AGENT,
// passed=false and notes as a PLAIN OBJECT `{flag: 'IC_AGENT_FLOW', ...}` —
// not the array shape the postpone notes use. The backend already lets these
// rows satisfy the KYC prerequisite (signature is allowed); this helper is the
// frontend mirror so the wizard grants the same passage. The check stays
// passed=false — KYC remains pending for ops to resolve out-of-flow. Keep in
// sync with lambdas/src/endpoint/utils/prerequisiteCheck.ts (case "KYC").

export const AGENT_SKIP_STAGE = 'SKIPPED_BY_AGENT';
export const AGENT_SKIP_FLAG = 'IC_AGENT_FLOW';

/** True if a cp_kyc_check.notes object carries the agent-skip flag. */
export function isAgentSkipNotes(notes: unknown): boolean {
  return !!notes && !Array.isArray(notes) && (notes as any).flag === AGENT_SKIP_FLAG;
}

/** True if a cp_kyc_check row is an agent skip the backend gate accepts. */
export function isAgentSkipCheck(check: { stage?: string | null; notes?: unknown } | null | undefined): boolean {
  return !!check && check.stage === AGENT_SKIP_STAGE && isAgentSkipNotes(check.notes);
}
